import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useCatalog } from "../lib/catalog-context";
import { filterPlugins, extractCategories, groupByAuthorMatch, collectIdentityNames, groupByIdentities } from "../lib/search";
import FilterBar from "../components/FilterBar";
import PluginCard from "../components/PluginCard";
import PluginListView from "../components/PluginListView";
import { formatDataThroughDate } from "../lib/adoption";
import CatalogToolbar from "../components/CatalogToolbar";
import type { SortOption, ViewMode } from "../components/CatalogToolbar";
import SearchBar from "../components/SearchBar";
import AiAnswer from "../components/AiAnswer";
import type { ResolvedPick } from "../components/AiAnswer";
import { fetchAiSearch, fetchSemanticHits, AiSearchError } from "../lib/ai-mode-client";
import type { AiSearchResponse } from "../lib/ai-mode-client";
import type { AiFailureReason } from "../lib/ai-mode";
import { login } from "../lib/auth";
import { resolvePicks } from "../lib/resolve-picks";
import { env } from "../lib/env";
import type { SemanticHit } from "../lib/hybrid-retrieval";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import GettingStarted from "../components/GettingStarted";
import type { ExtensionType, MarketplaceEntry, SupportedClient } from "../types/index";
import styles from "./CatalogPage.module.css";

const CLIENT_FILTER_OPTIONS: { value: SupportedClient; label: string }[] = [
  { value: "claude-chat",   label: "Claude Chat" },
  { value: "claude-cowork", label: "Claude Cowork" },
  { value: "claude-code",   label: "Claude Code" },
];

type TypeFilter = "all" | ExtensionType;

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: "all",    label: "All" },
  { value: "plugin", label: "Plugins" },
  { value: "skill",  label: "Skills" },
];

const SORT_ORDER_MAP: Record<SortOption, "asc" | "desc"> = {
  relevance: "desc", // unused — relevance keeps the search engine's order, no comparator runs
  topRated: "desc", // ignored by the topRated sort (unrated entries are always last)
  name: "asc",
  updated: "desc",
  created: "desc",
  official: "desc", // ignored by the official sort (Official-first is a fixed order)
  mostUsed: "desc", // ignored by the mostUsed sort (unmeasured entries are always last)
};

export const PAGE_SIZE = 30;

// Key for the "Other results" section in the jump-to-section summary. Chip sections are keyed
// by their label, unique because groupByIdentities dedupes identities; this can't collide
// with one.
const OTHER_SECTION_KEY = "__other__";

// groupByIdentities labels a section "Authored by X" or "Owning team: X"; the jump summary
// shows just X, matching the chip text the user picked.
const identityNameFromLabel = (label: string) => label.replace(/^(Authored by |Owning team: )/, "");

// Most Used ordering while author/team chips are active —
// usage first, then most recently updated, then A–Z (product decision on #735). Most entries
// have no usage yet (42.4% coverage), so plain Most Used leaves large ties that fall back to
// name order; Recently Updated breaks them so a team's newest skills don't sink below
// everything it published earlier. Scoped to chip searches only — browse Most Used keeps its
// existing tie order (see the "mostUsed" case in sortPlugins).
function compareUsageThenRecentThenName(a: MarketplaceEntry, b: MarketplaceEntry): number {
  const aUsers = a.adoption?.activeUsers30d;
  const bUsers = b.adoption?.activeUsers30d;
  if (aUsers !== bUsers) {
    // Unmeasured means "not measured", never zero — it sorts after every measured entry.
    if (aUsers === undefined) return 1;
    if (bUsers === undefined) return -1;
    return bUsers - aUsers;
  }
  // An unparseable date (the catalog is untrusted, rescan-rewritten input) sorts as oldest
  // rather than poisoning the comparison with NaN.
  const aParsed = Date.parse(a.lastUpdatedAt);
  const bParsed = Date.parse(b.lastUpdatedAt);
  const aTime = Number.isNaN(aParsed) ? -Infinity : aParsed;
  const bTime = Number.isNaN(bParsed) ? -Infinity : bParsed;
  if (aTime !== bTime) return bTime > aTime ? 1 : -1;
  return a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase());
}

function getEmptyStateMessage(
  query: string,
  clients: SupportedClient[],
  categories: string[],
  typeFilter: TypeFilter = "all",
  officialOnly = false,
): string {
  const noun =
    typeFilter === "plugin" ? "plug-ins"
    : typeFilter === "skill" ? "skills"
    : "plug-ins or skills";
  const parts: string[] = [];
  if (query) parts.push(`matching "${query}"`);
  if (officialOnly) parts.push("marked Official CAI");
  if (clients.length > 0) {
    const labels = clients.map((c) => CLIENT_FILTER_OPTIONS.find((o) => o.value === c)?.label ?? c);
    parts.push(`for ${labels.join(" or ")}`);
  }
  if (categories.length === 1) parts.push(`in ${categories[0]}`);
  else if (categories.length > 1) parts.push(`in ${categories.join(" or ")}`);
  return parts.length > 0 ? `No ${noun} ${parts.join(" ")}` : `No ${noun} available`;
}

interface CatalogPageProps {
  onSelectPlugin: (plugin: MarketplaceEntry) => void;
  initialClients?: string[];
  initialCategories?: string[];
  initialQuery?: string;
  initialType?: ExtensionType;
  initialOfficial?: boolean;
  // Multi-select author/org chips, restored the same way
  // as the other filters above — this page unmounts on the detail route, so without this the
  // chips reset to empty on browser-back exactly like initialQuery would without its own prop.
  initialIdentities?: string[];
  onHelp?: () => void;
  onFiltersChange?: (query: string, clients: SupportedClient[], categories: string[], type?: ExtensionType, official?: boolean, identities?: string[]) => void;
  // AI answer state (#626) is OPTIONALLY CONTROLLED. AppContent passes it in — lifting it there
  // so a paid-for judgement survives navigating into a plugin detail and back (this page unmounts
  // on the detail route; browser-back restores the query from the URL, but not component state).
  // When the props are omitted (e.g. a standalone render in tests) the page falls back to local
  // state and behaves exactly as before. All inert unless the AI UI is on (aiSearchEnabled), so
  // prod — where the gate is off — never populates them either way.
  aiRequested?: boolean;
  setAiRequested?: Dispatch<SetStateAction<boolean>>;
  aiResult?: AiSearchResponse | null;
  setAiResult?: Dispatch<SetStateAction<AiSearchResponse | null>>;
  aiResultQuery?: string | null;
  setAiResultQuery?: Dispatch<SetStateAction<string | null>>;
}

export default function CatalogPage({ onSelectPlugin, initialClients, initialCategories, initialQuery, initialType, initialOfficial, initialIdentities, onHelp, onFiltersChange, aiRequested: aiRequestedProp, setAiRequested: setAiRequestedProp, aiResult: aiResultProp, setAiResult: setAiResultProp, aiResultQuery: aiResultQueryProp, setAiResultQuery: setAiResultQueryProp }: CatalogPageProps) {
  // Catalog data from the shared provider (one fetch, one source of truth).
  const { plugins, isLoading, error, refresh, adoptionMeta } = useCatalog();
  // "" whenever there is no adoption artifact or the date is unparseable — the caption
  // is then omitted. Both cases are live today: #264 publishes a real artifact in
  // NON-PROD (adoption-refresh runs daily at 08:00 UTC), while prod leaves
  // `snowflake_usage_view` unset, so its Lambda is heartbeat-only and no artifact
  // exists. So this caption renders in non-prod and is absent in prod until the prod
  // usage view is enabled.
  const usageAsOf = adoptionMeta?.dataThroughDate ? formatDataThroughDate(adoptionMeta.dataThroughDate) : "";

  // Sorted view of the catalog — updated whenever the provider's plugins change.
  const allPlugins = useMemo(
    () =>
      [...plugins].sort((a, b) =>
        a.pluginName.toLowerCase().localeCompare(b.pluginName.toLowerCase()),
      ),
    [plugins],
  );

  // Author/org typeahead suggestions for SearchBar.
  const identitySuggestions = useMemo(() => collectIdentityNames(plugins), [plugins]);

  // Multi-select chips, OR'd together. selectedIdentities
  // only; identityGroups itself is computed further down, against `results` — once the other
  // filters (category/client/type/official/sort) are already applied — so chips compose with
  // them the same way the free-text groupByAuthorMatch path already does.
  const [selectedIdentities, setSelectedIdentities] = useState<string[]>(initialIdentities ?? []);

  // Categories derived from the full sorted list.
  const availableCategories = useMemo(() => extractCategories(allPlugins), [allPlugins]);

  const [clientFilters, setClientFilters] = useState<SupportedClient[]>((initialClients as SupportedClient[]) ?? []);
  const [categoryFilters, setCategoryFilters] = useState<string[]>(initialCategories ?? []);
  const [searchQuery, setSearchQuery] = useState(initialQuery ?? "");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(initialType ?? "all");
  // Two defaults, one per mode:
  //   browsing  -> Most Used   (product decision, 2026-08-10)
  //   searching -> Best Match  (product decision, 2026-08-19, shipped in #591)
  //
  // "Searching" means there is TEXT in the box. Author/team chips alone don't count: with no
  // text there is nothing to be relevant to, so a chip-only search stays on Most Used, and
  // clearing the text while keeping a chip returns to it (product decision on #735).
  //
  // Only the DEFAULT switches. Once the user picks a sort themselves it stays put through
  // typing, clearing, and adding/removing chips (product decision on #735). So this holds just
  // the explicit pick — null while still on the default — and the effective `sortBy` is derived
  // further down from the current query. Deriving it (rather than seeding a state and syncing
  // it with an effect) also means a shared link that already carries a query lands on Best
  // Match on the first frame instead of flashing Most Used.
  //
  // Entries with no usage are not hidden or lumped together arbitrarily: ties preserve
  // the incoming order, which while browsing is name order (allPlugins is sorted by
  // name below), so the tail of the list reads exactly as the Name sort would.
  //
  // NOTE: #266 AC 11 gates the Most Used default on measured coverage above 50%. The gate
  // FAILED and the default is flipped anyway — a deliberate override, not an oversight
  // (2026-08-10, Bryce + Veronica; see the PR description).
  //
  // The override was agreed against the 40.7% pre-production spike. #264's producer has
  // since measured the real figure at 42.4% (649/1532 entries, see contract-7-adoption.ts):
  // the gate still fails, by the same margin and for the same structural reason — Anthropic
  // counts a skill only when explicitly invoked, so reference skills never register. That
  // 42.4% is close to the ceiling for a per-skill metric, so this waiver is not waiting on
  // a matching improvement that would let the gate pass on its own terms.
  const [userSort, setUserSort] = useState<SortOption | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [officialOnly, setOfficialOnly] = useState(initialOfficial ?? false);
  const [searchBarClearToken, setSearchBarClearToken] = useState(0);

  // UI gate for AI mode (the "Ask AI" affordance, its panel and the AI status line). Off unless
  // the build set REACT_APP_AI_SEARCH_ENABLED="true" — currently off in every env while the
  // feature is paused. Keeps a non-functional "Ask AI" button off any environment whose /api
  // isn't wired.
  const aiSearchEnabled = env.REACT_APP_AI_SEARCH_ENABLED === "true";
  // Gate for hybrid search in the plain box (the /api/semantic fetch fused with lexical). Its
  // own flag so semantic can ship while Ask AI stays paused. Ask AI implies it: Ask AI needs the
  // same backend, and turning AI back on must not silently drop the plain box to lexical-only.
  const semanticSearchEnabled =
    env.REACT_APP_SEMANTIC_SEARCH_ENABLED === "true" || aiSearchEnabled;

  /**
   * Semantic hits for the PLAIN search box (#626), fused with lexical into ONE ranking.
   *
   * Deliberately NOT progressive: the box waits for these (~600ms, ordinary search latency)
   * and renders once. Showing lexical first and re-ranking on arrival would move results while
   * someone is reading them, which is worse than a short wait — and it is only a temptation if
   * you are trying to avoid the wait, which at 600ms is not worth avoiding.
   *
   * `fetchSemanticHits` never throws, so a missing backend degrades to lexical-only search
   * rather than breaking the page. `isSemanticLoading` exists so the UI can say "searching"
   * instead of flashing lexical-only results that are about to change.
   */
  const [semanticHits, setSemanticHits] = useState<SemanticHit[]>([]);
  const [isSemanticLoading, setIsSemanticLoading] = useState(false);

  useEffect(() => {
    // Gated on the semantic-search flag: where the /api backend isn't wired, don't fire a
    // semantic fetch that would only 404 and degrade to lexical anyway. Off → no hits, pure
    // lexical, no /api traffic.
    if (!semanticSearchEnabled || !searchQuery.trim()) {
      setSemanticHits([]);
      setIsSemanticLoading(false);
      return;
    }
    const controller = new AbortController();
    // Clear rather than keep the previous query's hits: stale hits fused into a new query's
    // lexical results would look like matches for the new query.
    setSemanticHits([]);
    setIsSemanticLoading(true);
    void fetchSemanticHits(searchQuery, controller.signal).then((hits) => {
      if (controller.signal.aborted) return;
      setSemanticHits(hits);
      setIsSemanticLoading(false);
    });
    return () => controller.abort();
  }, [semanticSearchEnabled, searchQuery]);

  /**
   * AI mode (#626) as a per-query ACTION rather than a persistent mode.
   *
   * Deliberately resets on every query change. A sticky toggle would silently charge 10-30s and
   * tokens for the next search too — including searches where plain results are measurably
   * better (`exact-name` is 100% for retrieval alone, and retrieval's top 6 beats the model's
   * 6 picks: 88% vs 86%). Opting in per query keeps the cost where the user asked for it.
   *
   * `aiRequested`, `aiResult` and `aiResultQuery` are PROPS, lifted to AppContent: this page
   * unmounts when a pick opens a plugin detail, so local state would be destroyed on the way
   * back (browser-back restores the query from the URL, but not component state). `aiResultQuery`
   * is the CACHE KEY — the trimmed query `aiResult` answers — so a paid-for judgement (9-30s and
   * tokens) is kept even after the panel is dismissed and reused when the SAME query reopens AI
   * mode; it is null whenever there is no cached result. `aiLoading`/`aiError` stay LOCAL: a
   * fetch mid-navigation is rare and simply re-derives (a cache hit on remount, or a refetch).
   */
  // Optionally controlled: use the lifted props from AppContent when present (so the answer
  // outlives this page's unmount), else fall back to local state. `aiStateControlled` keys off
  // a setter's presence — not a value's — because the values can legitimately be null/false.
  const [localAiRequested, setLocalAiRequested] = useState(false);
  const [localAiResult, setLocalAiResult] = useState<AiSearchResponse | null>(null);
  const [localAiResultQuery, setLocalAiResultQuery] = useState<string | null>(null);
  const aiStateControlled = setAiRequestedProp !== undefined;
  const aiRequested = aiStateControlled ? aiRequestedProp ?? false : localAiRequested;
  const setAiRequested = setAiRequestedProp ?? setLocalAiRequested;
  const aiResult = aiStateControlled ? aiResultProp ?? null : localAiResult;
  const setAiResult = setAiResultProp ?? setLocalAiResult;
  const aiResultQuery = aiStateControlled ? aiResultQueryProp ?? null : localAiResultQuery;
  const setAiResultQuery = setAiResultQueryProp ?? setLocalAiResultQuery;

  const [aiLoading, setAiLoading] = useState(false);
  const [aiFailure, setAiFailure] = useState<{ reason: AiFailureReason; correlationId?: string } | null>(null);
  // Bumped by the error panel's "Try again" to force a refetch of the current query. Needed
  // because on error there is no cached result to invalidate — only a new signal re-runs the
  // effect. (Creds expire ~hourly, so a retry-without-retyping is a routine need.)
  const [aiRetryToken, setAiRetryToken] = useState(0);
  // A paid judge call is authorized ONLY by an explicit user gesture (clicking Ask AI / Try
  // again), which arms THIS query here. LOCAL on purpose: it resets to null on every (re)mount,
  // so a lifted `aiRequested` that survives navigation can RESTORE a cached answer but can never
  // START a new call for a query the user never pointed AI at (Katie, #659 review round 2).
  const [fetchArmedQuery, setFetchArmedQuery] = useState<string | null>(null);

  const trimmedQuery = searchQuery.trim();

  // Fetch a judgement whenever AI mode is on and there is a query. The request goes to a
  // backend because embedding the query needs Bedrock credentials a browser cannot hold.
  //
  // Aborting on change is load-bearing, not hygiene: the judge takes 9-30s, so without it a
  // slow earlier request can resolve AFTER a newer one and overwrite the answer with a stale
  // one — the classic race, made much more likely here by the latency.
  //
  // CACHING. Dismiss sets aiRequested=false but must NOT throw away the answer, so this effect
  // no longer nulls aiResult when it stands down — it keeps the cached (aiResult, aiResultQuery)
  // pair. When AI mode is (re)opened on a query the cache already answers, it reuses it and
  // does not refetch. A genuine query change trips the cache key (aiResultQuery !== trimmed),
  // so a stale answer is never reused — and it is never SHOWN for the wrong query either,
  // because the render below gates on the same key.
  useEffect(() => {
    if (!aiSearchEnabled || !aiRequested || !trimmedQuery) {
      // Panel is hidden (its render is gated on aiSearchEnabled && aiRequested && hasQuery);
      // leave aiResult in place as the cache. Only clear the transient loading/error flags.
      setAiFailure(null);
      setAiLoading(false);
      return;
    }
    // Cache hit: the current result already answers this exact query. Reuse it, no refetch.
    // On a browser-back RESTORE this is the ONLY branch that runs, because `fetchArmedQuery` is
    // local and resets to null on the fresh mount.
    if (aiResult && aiResultQuery === trimmedQuery) {
      setAiFailure(null);
      setAiLoading(false);
      return;
    }
    // No cached answer for this query. Fetch ONLY if the user deliberately armed THIS query this
    // mount (clicked Ask AI / Try again — see requestAiSearch). `aiRequested` is lifted, so it can
    // be true on a remount for a DIFFERENT query (browser-nav to another catalog URL) or after an
    // aborted in-flight call; neither authorizes another 9-30s judge call, so both fall here and
    // restore-or-nothing instead of auto-firing. (The mount guard below drops the stale flag.)
    if (fetchArmedQuery !== trimmedQuery) {
      setAiFailure(null);
      setAiLoading(false);
      return;
    }
    const controller = new AbortController();
    setAiLoading(true);
    setAiFailure(null);
    fetchAiSearch(trimmedQuery, controller.signal)
      .then((res) => {
        setAiResult(res);
        setAiResultQuery(trimmedQuery);
        setAiLoading(false);
      })
      .catch((err: Error) => {
        // An abort is us superseding our own request, not a failure to report.
        if (err.name === "AbortError") return;
        // A failed request carries a typed reason + correlation id (AiSearchError). Anything else
        // (shouldn't happen — fetchAiSearch wraps everything) falls back to a generic failure. The
        // technical detail goes to the console, never the panel.
        if (err instanceof AiSearchError) {
          setAiFailure({ reason: err.reason, correlationId: err.correlationId });
        } else {
          setAiFailure({ reason: "generic" });
        }
        console.warn("AI search failed", err);
        setAiResult(null);
        setAiResultQuery(null);
        setAiLoading(false);
      });
    return () => controller.abort();
    // setAiResult/setAiResultQuery are stable setters (either the lifted props or the local
    // fallbacks, both from useState), but they are now derived consts rather than raw useState
    // returns, so exhaustive-deps can no longer infer their stability — list them explicitly.
  }, [aiSearchEnabled, aiRequested, trimmedQuery, aiRetryToken, aiResult, aiResultQuery, fetchArmedQuery, setAiResult, setAiResultQuery]);

  // On (re)mount, a lifted `aiRequested` may still be true from a PRIOR query — it outlives this
  // page's unmount. Honor it only as a RESTORE: keep it when a cached answer matches the current
  // query, otherwise drop it, so browser navigation neither shows a blank panel nor (together
  // with the fetch guard above) spends on a query the user never asked AI. A fresh Ask AI click
  // re-arms and re-opens. This is the "restore only, never re-fetch" rule Katie asked for (#659).
  useEffect(() => {
    if (aiRequested && !(aiResult && aiResultQuery === trimmedQuery)) {
      setAiRequested(false);
    }
    // Mount-only: the one-time restore/clear decision for the lifted flag. It must NOT re-run on
    // query/cache changes mid-mount — handleSearch and the fetch effect own those.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The cached result, but only when it actually answers the CURRENT query — so a stale answer
  // for a previous query can never flash into the panel for a new one (the effect refetches,
  // but this gate covers the frame before it runs).
  const aiResultForQuery = useMemo(
    () => (aiResult && aiResultQuery === trimmedQuery ? aiResult : null),
    [aiResult, aiResultQuery, trimmedQuery],
  );

  /**
   * The model's picks resolved to catalog entries, in the model's order.
   *
   * Resolved here rather than in the panel so the panel can show a human name and navigate to
   * the same detail view a card opens — the model works in `installName` (the allowlist key),
   * which is machine-facing and often unreadable (`pr--skills--pr-review`).
   *
   */
  const resolvedPicks = useMemo<ResolvedPick[]>(() => {
    if (!aiResultForQuery?.judgement) return [];
    return resolvePicks(aiResultForQuery.candidates, aiResultForQuery.judgement.picks);
  }, [aiResultForQuery]);

  // The single failure the panel renders, from EITHER of two paths: a request that threw
  // (`aiFailure`, carrying its status-derived reason + correlation id) or a 200 that degraded
  // server-side (`aiUnavailable` on the cached result). A thrown failure wins; they are mutually
  // exclusive in practice (a success clears aiFailure; a throw nulls the cached result).
  const aiFailureForQuery: { reason: AiFailureReason; correlationId?: string } | null =
    aiFailure ??
    (aiResultForQuery?.aiUnavailable
      ? {
          reason: aiResultForQuery.aiUnavailableReason ?? "generic",
          correlationId: aiResultForQuery.correlationId,
        }
      : null);

  const hasQuery = searchQuery.trim().length > 0;
  // The effective sort: the user's explicit pick if they made one, else the default for the
  // current mode (see the userSort comment above). Best Match only exists while there's text
  // — the toolbar hides it otherwise — so an explicit Best Match pick falls back to the default
  // once the text clears, rather than leaving the dropdown on an option it no longer shows.
  const defaultSort: SortOption = hasQuery ? "relevance" : "mostUsed";
  const sortBy: SortOption = userSort && (userSort !== "relevance" || hasQuery) ? userSort : defaultSort;
  const tagsActive = selectedIdentities.length > 0;

  // Filtered results derived from the catalog + active filters. Deriving (rather
  // than holding in state and syncing via effects) keeps results correct by
  // construction — including after a refresh() that updates plugins in place.
  const results = useMemo(() => {
    const filtered = filterPlugins(allPlugins, {
      query: searchQuery,
      // Strict match first, broaden to OR only when strict returns nothing (#801).
      // Without this the default is "strict", so a multi-word query with no single
      // entry containing every word returns zero — the exact regression this PR fixes.
      mode: "auto",
      supportedClients: clientFilters.length > 0 ? clientFilters : undefined,
      categories: categoryFilters.length > 0 ? categoryFilters : undefined,
      extensionType: typeFilter === "all" ? undefined : typeFilter,
      officialOnly,
      sortBy,
      sortOrder: SORT_ORDER_MAP[sortBy],
      semanticHits,
    });
    // Chip searches break Most Used ties by recency, then
    // name (see compareUsageThenRecentThenName). Applied here, BEFORE groupByIdentities splits
    // the results, so every chip section — and "Other results" — gets the same ordering, and
    // the section order itself stays the chip order.
    return tagsActive && sortBy === "mostUsed" ? [...filtered].sort(compareUsageThenRecentThenName) : filtered;
  }, [allPlugins, searchQuery, clientFilters, categoryFilters, typeFilter, sortBy, officialOnly, semanticHits, tagsActive]);

  // Built from `results`, not `allPlugins` — so the chips
  // narrow the ALREADY-filtered-and-sorted set, composing with category/client/type/official/
  // sort exactly like the single-query groupByAuthorMatch path below already does.
  const identityGroups = useMemo(
    () => groupByIdentities(results, selectedIdentities),
    [results, selectedIdentities],
  );

  // The catalog with the page's filters applied but no text query: where a typed person or
  // team name looks for matches beyond what text search returned (see groupByAuthorMatch's
  // `pool`). Independent of the query, so it only recomputes when a filter changes.
  const queryFreePool = useMemo(
    () =>
      filterPlugins(allPlugins, {
        query: "",
        supportedClients: clientFilters.length > 0 ? clientFilters : undefined,
        categories: categoryFilters.length > 0 ? categoryFilters : undefined,
        extensionType: typeFilter === "all" ? undefined : typeFilter,
        officialOnly,
        // Best Match has no meaning without a query; Most Used gives pool-only matches a
        // useful order instead of raw catalog order.
        sortBy: sortBy === "relevance" ? "mostUsed" : sortBy,
        sortOrder: SORT_ORDER_MAP[sortBy === "relevance" ? "mostUsed" : sortBy],
      }),
    [allPlugins, clientFilters, categoryFilters, typeFilter, officialOnly, sortBy],
  );

  // Lift entries whose contributor/author/org matches the typed query into a labeled section.
  // Matches come from `results` plus the query-free pool, so typing "Katie Gould" also finds
  // her web uploads (contributorName isn't text-indexed). Grid view groups; list view shows
  // the same entries ungrouped. Chips take over when any are picked.
  const authorGroup = useMemo(
    () => (tagsActive ? null : groupByAuthorMatch(results, searchQuery, queryFreePool)),
    [tagsActive, results, searchQuery, queryFreePool],
  );

  // Author matches text search didn't return. They show in both views, so they count toward
  // the result total, the empty state and infinite scroll.
  const extraAuthorMatches = useMemo(() => {
    if (!authorGroup) return [];
    const inResults = new Set(results);
    return authorGroup.matches.filter((e) => !inResults.has(e));
  }, [authorGroup, results]);

  // Unique entries on the page.
  const shownCount = results.length + extraAuthorMatches.length;

  // How many cards the page renders in total. A skill matching two chips shows in both
  // sections, so with chips active this can exceed results.length (which counts unique
  // skills — that's the "N results" total in the summary). The load-more window is sized
  // against what's rendered, or the last few cards of a chip search would never load.
  const renderedTotal = tagsActive
    ? identityGroups.groups.reduce((n, g) => n + g.matches.length, 0) + identityGroups.rest.length
    : shownCount;

  // Generalizes the single-group windowing below
  // (visibleAuthorMatches/visibleAuthorRest) to N chip groups — same technique Katie's
  // review landed for the single-group case: slice the full concatenation ONCE by
  // visibleCount, then split that window back into per-group + rest pieces, so a popular
  // pick (e.g. a 141-entry org) can't synchronously mount every card on first paint, and
  // the "load more" sentinel's total stays tied to visibleCount the same way it already
  // is for the single-query path and the plain grid.
  const visibleIdentitySections = useMemo(() => {
    const windowed = [...identityGroups.groups.flatMap((g) => g.matches), ...identityGroups.rest].slice(
      0,
      visibleCount,
    );
    let offset = 0;
    const groups = identityGroups.groups.map((g) => {
      const visible = windowed.slice(offset, offset + g.matches.length);
      offset += g.matches.length;
      return { label: g.label, totalCount: g.matches.length, visible };
    });
    return { groups, visibleRest: windowed.slice(offset) };
  }, [identityGroups, visibleCount]);

  // One-line jump-to-section summary above the chip-grouped results — "Katie Gould (8) ·
  // Platform (7) · Other (1000)" — so a stack of 4-5 chips doesn't force a long scroll to find
  // out what each section holds or to reach a later one. Each count is that chip's own total; a
  // skill shared by two chips counts toward both, while the leading "N results" is unique skills.
  //
  // Listed in CHIP order (the order the user picked them, same as the sections), including a
  // chip whose section came back empty (e.g. every match filtered out) — groupByIdentities
  // omits empty groups, so without the "(0)" entry that chip's section would just silently not
  // exist. `start` is the section's offset into the windowed concatenation, used by
  // jumpToSection to grow visibleCount far enough to cover it.
  const sectionSummary = useMemo(() => {
    if (selectedIdentities.length === 0) return [];
    const byName = new Map<string, { key: string; count: number; start: number }>();
    let offset = 0;
    for (const g of identityGroups.groups) {
      byName.set(identityNameFromLabel(g.label).trim().toLowerCase(), {
        key: g.label,
        count: g.matches.length,
        start: offset,
      });
      offset += g.matches.length;
    }
    const items = selectedIdentities.map((name) => {
      const hit = byName.get(name.trim().toLowerCase());
      return { key: hit?.key ?? `empty:${name}`, name, count: hit?.count ?? 0, start: hit?.start ?? -1 };
    });
    if (identityGroups.rest.length > 0) {
      items.push({ key: OTHER_SECTION_KEY, name: "Other", count: identityGroups.rest.length, start: offset });
    }
    return items;
  }, [identityGroups, selectedIdentities]);

  // Section headings by key, so a summary click can find its target. Callback refs keep this
  // in sync as sections mount/unmount when chips change.
  const sectionHeadingRefs = useRef(new Map<string, HTMLHeadingElement>());
  const registerSectionHeading = (key: string) => (el: HTMLHeadingElement | null) => {
    if (el) sectionHeadingRefs.current.set(key, el);
    else sectionHeadingRefs.current.delete(key);
  };

  // A fresh object per click (not just the key), so clicking the same item twice still re-runs
  // the scroll effect.
  const [pendingJump, setPendingJump] = useState<{ key: string } | null>(null);

  // Sections are windowed by visibleCount, so before scrolling, grow the window to cover
  // everything ABOVE the target plus one page of the target itself. Without this, jumping to
  // a later section scrolls to its heading while an earlier section is still partly unloaded
  // — the sentinel then loads more of that EARLIER section (the window fills in order), pushing
  // the target heading back down out from under the user. The scroll itself runs in the effect
  // below, after the grown window has committed, so it lands on the heading's final position.
  const jumpToSection = (key: string, start: number) => {
    if (start < 0) return;
    setVisibleCount((c) => Math.max(c, Math.min(start + PAGE_SIZE, renderedTotal)));
    setPendingJump({ key });
  };

  useEffect(() => {
    if (!pendingJump) return;
    const heading = sectionHeadingRefs.current.get(pendingJump.key);
    setPendingJump(null);
    if (!heading) return;
    const reduceMotion =
      typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (typeof heading.scrollIntoView === "function") {
      heading.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    }
    // Move focus too, so keyboard and screen-reader users land in the section they jumped to
    // rather than staying up on the summary. preventScroll: the scroll above already placed it.
    heading.focus({ preventScroll: true });
  }, [pendingJump]);

  // Sync active filters to the URL on user changes. Skip the initial render so we
  // don't echo the URL-derived seed values straight back. Sort is intentionally
  // not synced (matches prior behaviour).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    onFiltersChange?.(
      searchQuery,
      clientFilters,
      categoryFilters,
      typeFilter === "all" ? undefined : typeFilter,
      officialOnly,
      selectedIdentities,
    );
    // onFiltersChange identity is not a filter input; fire only on filter changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly, selectedIdentities]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    // A new query is a new question: don't carry a paid-for AI answer onto it, and DISARM any
    // pending fetch so the next call only fires when the user asks for THIS query.
    setAiRequested(false);
    setFetchArmedQuery(null);
  };

  // Pressing Ask AI (now inside the search bar) is the ONLY thing that authorizes a paid judge
  // call: it syncs the query, opens the panel, AND arms the fetch for exactly that query. The arm
  // is separate from `aiRequested` because the latter is lifted and must never, by itself, trigger
  // a call (see the fetch effect). SearchBar hands the fresh box value up here, so the armed query
  // can never lag the search debounce. Try again re-arms via `trimmedQuery` (see AiAnswer.onRetry).
  const requestAiSearch = (rawQuery: string) => {
    const query = rawQuery.trim();
    if (!query) return;
    setSearchQuery(rawQuery);
    setAiRequested(true);
    setFetchArmedQuery(query);
  };

  // Reset the visible window to the first page whenever the filtered results change.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [results]);

  // Page the matches+rest split exactly like the ungrouped grid pages `results`: slice the
  // CONCATENATION once by visibleCount, then divide that slice back into the two sections.
  // Slicing each section independently (matches unsliced, rest sliced against visibleCount)
  // renders every match in one go for a popular author/org — the PR's own manual test hit
  // 223 cards on first paint — and leaves the "load more" sentinel spinning once rest is
  // exhausted, since visibleCount keeps growing past what rest has left to reveal. Slicing
  // the concatenation keeps the total rendered count tied to visibleCount the same way the
  // ungrouped grid already is.
  const { visibleAuthorMatches, visibleAuthorRest } = useMemo(() => {
    if (!authorGroup) return { visibleAuthorMatches: [], visibleAuthorRest: [] };
    const windowed = [...authorGroup.matches, ...authorGroup.rest].slice(0, visibleCount);
    return {
      visibleAuthorMatches: windowed.slice(0, authorGroup.matches.length),
      visibleAuthorRest: windowed.slice(authorGroup.matches.length),
    };
  }, [authorGroup, visibleCount]);

  const sentinelVisible = visibleCount < renderedTotal;
  // Keyed on `sentinelVisible` (a boolean), not `visibleCount`: the observer is
  // recreated only when crossing the "more to show / nothing more" boundary, so
  // each PAGE_SIZE grow reuses the same observer instead of tearing it down and
  // rebuilding it every batch. Keying on `visibleCount` regressed once (commit
  // d762e41): because visibleCount changes on every grow, the effect re-ran each
  // batch — disconnecting and rebuilding a fresh IntersectionObserver every time
  // instead of leaving one in place. The boolean key holds it stable across grows.
  //
  // The sentinel is rendered exactly once, as a fixed sibling after the view/group
  // conditional below — not duplicated inside each branch — so switching between list
  // view, the grouped grid, and the plain grid never remounts it; the observer stays
  // bound to the same connected node throughout.
  useEffect(() => {
    if (!sentinelVisible) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + PAGE_SIZE, renderedTotal));
        }
      },
      { rootMargin: "400px" }, // prefetch the next page ~400px before the sentinel scrolls into view
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinelVisible, renderedTotal]);

  const toggleFilter = <T,>(prev: T[], value: T, eq: (a: T, b: T) => boolean): T[] =>
    prev.some((v) => eq(v, value)) ? prev.filter((v) => !eq(v, value)) : [...prev, value];

  const handleClientToggle = (client: SupportedClient) =>
    setClientFilters((prev) => toggleFilter(prev, client, (a, b) => a === b));

  const handleCategoryToggle = (category: string) =>
    setCategoryFilters((prev) =>
      toggleFilter(prev, category, (a, b) => a.toLowerCase() === b.toLowerCase()),
    );

  const handleTypeChange = (type: TypeFilter) => setTypeFilter(type);

  // An explicit pick from the toolbar — from here on the sort no longer auto-switches.
  const handleSortChange = (sort: SortOption) => setUserSort(sort);

  const handleOfficialToggle = () => setOfficialOnly((prev) => !prev);

  if (error) {
    return (
      <div className={styles.errorState}>
        <h1>Error Loading Marketplace</h1>
        <p className={styles.errorMessage}>{error}</p>
        <button onClick={() => refresh()} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.header}>
        <h1>AI Marketplace</h1>
        <p className={styles.subtitle}>
          Browse and discover available AI plug-ins and skills for Cox Auto approved LLM clients.
        </p>
      </div>

      <GettingStarted onHelp={onHelp} />
      <SearchBar
        onSearch={handleSearch}
        isLoading={isLoading}
        externalQuery={searchQuery}
        clearToken={searchBarClearToken}
        // "or ask a question" only when the Ask AI affordance is actually shown. With AI paused
        // (aiSearchEnabled off) there is no way to ask, so inviting it in the placeholder is
        // misleading — fall back to the plain search prompt.
        placeholder={
          aiSearchEnabled
            ? "Search plug-ins, skills, authors, or teams, or ask a question..."
            : "Search plug-ins, skills, authors, or teams..."
        }
        aiEnabled={aiSearchEnabled}
        onAskAi={requestAiSearch}
        suggestions={identitySuggestions}
        selectedIdentities={selectedIdentities}
        onIdentitiesChange={setSelectedIdentities}
      />

      {/*
        AI mode is an ACTION on the current query, not a mode you switch into — the measurements
        support that framing (retrieval already puts the answer in the top 6 for 88% of cases; AI
        earns its 10-30s on constraints, explanations and refusals). The entry point now lives
        INSIDE the search bar above (one input, one place to type — the Notion / Algolia / Ask
        Copilot convention), and the plain results stay rendered BELOW the AI panel so the answer
        is additive.

        This line keeps the result-count status the old prompt row carried (X results / searching /
        a no-match nudge), plus a short hint while the box is empty so the AI affordance stays
        discoverable. Gated on aiSearchEnabled, so environments with AI off (prod today) are
        unchanged — the whole line, like the old row, simply does not render there.
      */}
      {aiSearchEnabled && (
        <p className={styles.searchStatus}>
          {!hasQuery
            ? "Results narrow as you type — or ask AI in your own words."
            : isSemanticLoading
              ? "Searching…"
              : shownCount === 0
                // Zero results is arguably AI mode's BEST case, not an edge case: the model can
                // state plainly that nothing does this, which retrieval cannot (0 of 9 correct
                // rejections). So nudge toward it rather than hiding the option.
                ? "No matches — Ask AI can tell you whether anything comes close"
                : `${shownCount} ${shownCount === 1 ? "result" : "results"}`}
        </p>
      )}

      {aiSearchEnabled && aiRequested && hasQuery && (
        <AiAnswer
          judgement={aiResultForQuery?.judgement ?? null}
          picks={resolvedPicks}
          isLoading={aiLoading}
          // One failure signal for every kind — a thrown request (busy/session_expired/timeout/
          // generic) OR a 200 server degrade (truncated/timeout/generic). The panel shows one
          // calm line per reason with the plain results below; raw errors go to the console only.
          failureReason={aiFailureForQuery?.reason ?? null}
          correlationId={aiFailureForQuery?.correlationId}
          // Only used by the session_expired reason — starts a fresh MSAL sign-in.
          onSignIn={() => void login()}
          onSelect={onSelectPlugin}
          // Dismiss stops SHOWING the panel but keeps the cached answer (see the AI effect):
          // reopening the same query reuses it rather than paying for another call.
          onDismiss={() => setAiRequested(false)}
          // Re-run the request for the current query. The Ask-AI row is hidden while an error
          // or unavailable notice is showing, so without this there is no way back short of
          // retyping the query.
          //
          // Clearing the cached result FIRST is load-bearing for the aiUnavailable notice: a
          // degrade is a 200, so it is cached via the success path exactly like a real answer,
          // and the fetch effect's cache-hit check fires BEFORE the fetchArmedQuery check — so
          // without this, "Try again" would hit the cache and never re-fetch. The genuine-error
          // path nulls the cache in its own .catch and so retries fine; a 200 degrade does not,
          // hence the explicit clear here. Then re-arm and bump the token to force the refetch.
          onRetry={() => {
            setAiResult(null);
            setAiResultQuery(null);
            setFetchArmedQuery(trimmedQuery);
            setAiRetryToken((t) => t + 1);
          }}
        />
      )}

      <p className={styles.totals}>
        Total available: {allPlugins.filter((p) => p.extensionType === "skill").length} skills · {allPlugins.filter((p) => p.extensionType === "plugin").length} plug-ins
      </p>

      <CatalogToolbar
        sortBy={sortBy}
        onSortChange={handleSortChange}
        showRelevance={hasQuery}
        viewMode={viewMode}
        onViewChange={setViewMode}
        officialOnly={officialOnly}
        onOfficialToggle={handleOfficialToggle}
      >
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Type:
          </span>
          <div role="group" aria-label="Filter by type" className={styles.toggleGroup}>
            {TYPE_FILTER_OPTIONS.map((opt) => {
              const active = typeFilter === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleTypeChange(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.filterRow}>
          <span className={styles.filterLabel}>
            Works with:
          </span>
          <div role="group" aria-label="Filter by client" className={styles.toggleGroup}>
            {CLIENT_FILTER_OPTIONS.map((opt) => {
              const active = clientFilters.includes(opt.value);
              return (
                <button
                  key={opt.value}
                  onClick={() => handleClientToggle(opt.value)}
                  aria-pressed={active}
                  className={`${styles.toggleButton}${active ? ` ${styles.active}` : ""}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        <FilterBar
          categories={availableCategories}
          activeCategories={categoryFilters}
          onCategoryToggle={handleCategoryToggle}
          labelMinWidth="6rem"
        />
      </CatalogToolbar>

      {/* Stated once here rather than repeated on every card: the window lags "now" by
          ~3 days, and #266 requires that be legible wherever the figure appears. This
          line sits above both the grid and the list view, so it covers both. */}
      {usageAsOf && (
        <p className={styles.usageAsOf}>Usage data as of {usageAsOf}</p>
      )}

      {isLoading && !allPlugins.length ? (
        <Loading message="Loading catalog..." />
      ) : isSemanticLoading ? (
        // Hold the grid until semantic hits land (~600ms), rather than painting lexical-only
        // results and re-ranking underneath the reader. Both halves of retrieval produce ONE
        // ranking, so showing half of it is showing a different ranking — and a result moving
        // while someone is looking at it is worse than a brief wait. This is the whole reason
        // the append tier was abandoned; rendering early would reintroduce the same problem.
        <Loading message="Searching..." />
      ) : shownCount === 0 ? (
        <EmptyState message={getEmptyStateMessage(searchQuery, clientFilters, categoryFilters, typeFilter, officialOnly)}>
          {(searchQuery || clientFilters.length > 0 || categoryFilters.length > 0 || typeFilter !== "all" || officialOnly) && (
            <button
              onClick={() => {
                setClientFilters([]);
                setCategoryFilters([]);
                setSearchQuery("");
                // Clearing the query is a new question, same as handleSearch("") — stand the
                // AI panel down AND disarm any pending fetch. Inert today (emptying the query
                // hides the panel anyway), but this is a second path to the same state and must
                // not drift from it.
                setAiRequested(false);
                setFetchArmedQuery(null);
                setTypeFilter("all");
                setOfficialOnly(false);
                setSelectedIdentities([]);
                setSearchBarClearToken((t) => t + 1);
              }}
              className={styles.clearButton}
            >
              Clear filters
            </button>
          )}
        </EmptyState>
      ) : (
        <>
          {selectedIdentities.length > 0 ? (
            // One section per picked chip, OR'd together,
            // windowed via visibleIdentitySections so a popular pick can't mount hundreds of
            // cards on first paint. Respects viewMode the same way the single-query path below
            // does, so switching to list view while chips are active shows the same sections,
            // just in the list layout.
            <>
              {/* Jump-to-section summary (see sectionSummary). Sticky, so it stays reachable
                  from deep inside a long section instead of only at the top. */}
              <nav aria-label="Jump to section" className={styles.sectionSummary}>
                <span className={styles.sectionSummaryLabel}>
                  {results.length} {results.length === 1 ? "result" : "results"}:
                </span>
                <ul className={styles.sectionSummaryList}>
                  {sectionSummary.map((item) => (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={styles.sectionSummaryLink}
                        disabled={item.count === 0}
                        title={item.count === 0 ? "No results with the current filters" : undefined}
                        aria-label={item.count === 0 ? `${item.name}: no results` : `Jump to ${item.name} (${item.count})`}
                        onClick={() => jumpToSection(item.key, item.start)}
                      >
                        {item.name} <span className={styles.sectionSummaryCount}>({item.count})</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
              {/* A section renders only once some of its cards are inside the loaded window, so a
                  later chip doesn't show a heading over nothing. jumpToSection grows the window
                  before scrolling, so its target heading is mounted by then. */}
              {visibleIdentitySections.groups.filter((group) => group.visible.length > 0).map((group) => (
                <div key={group.label}>
                  <h2
                    ref={registerSectionHeading(group.label)}
                    tabIndex={-1}
                    className={`${styles.sectionHeading} ${styles.sectionAnchor}`}
                  >
                    {group.label} ({group.totalCount})
                  </h2>
                  {viewMode === "list" ? (
                    <PluginListView plugins={group.visible} onSelectPlugin={onSelectPlugin} />
                  ) : (
                    <div className={styles.grid}>
                      {group.visible.map((plugin) => (
                        <PluginCard
                          // Prefixed by section: a skill matching two chips renders in both.
                          key={`${group.label}:${plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}`}
                          plugin={plugin}
                          onClick={() => onSelectPlugin(plugin)}
                          onClientClick={handleClientToggle}
                          onCategoryClick={handleCategoryToggle}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {visibleIdentitySections.visibleRest.length > 0 && (
                <>
                  <h2
                    ref={registerSectionHeading(OTHER_SECTION_KEY)}
                    tabIndex={-1}
                    className={`${styles.sectionHeading} ${styles.sectionAnchor}`}
                  >
                    Other results ({identityGroups.rest.length})
                  </h2>
                  {viewMode === "list" ? (
                    <PluginListView plugins={visibleIdentitySections.visibleRest} onSelectPlugin={onSelectPlugin} />
                  ) : (
                    <div className={styles.grid}>
                      {visibleIdentitySections.visibleRest.map((plugin) => (
                        <PluginCard
                          key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                          plugin={plugin}
                          onClick={() => onSelectPlugin(plugin)}
                          onClientClick={handleClientToggle}
                          onCategoryClick={handleCategoryToggle}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          ) : viewMode === "list" ? (
            <PluginListView plugins={[...results, ...extraAuthorMatches].slice(0, visibleCount)} onSelectPlugin={onSelectPlugin} />
          ) : authorGroup ? (
            <>
              <h2 className={styles.sectionHeading}>
                {authorGroup.label} ({authorGroup.matches.length})
              </h2>
              <div className={styles.grid}>
                {visibleAuthorMatches.map((plugin) => (
                  <PluginCard
                    key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                    plugin={plugin}
                    onClick={() => onSelectPlugin(plugin)}
                    onClientClick={handleClientToggle}
                    onCategoryClick={handleCategoryToggle}
                  />
                ))}
              </div>
              {visibleAuthorRest.length > 0 && (
                <>
                  <h2 className={styles.sectionHeading}>Other results ({authorGroup.rest.length})</h2>
                  <div className={styles.grid}>
                    {visibleAuthorRest.map((plugin) => (
                      <PluginCard
                        key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                        plugin={plugin}
                        onClick={() => onSelectPlugin(plugin)}
                        onClientClick={handleClientToggle}
                        onCategoryClick={handleCategoryToggle}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className={styles.grid}>
              {results.slice(0, visibleCount).map((plugin) => (
                <PluginCard
                  key={plugin.installName ?? `${plugin.source}--${plugin.orgOrOwner}--${plugin.repoName}`}
                  plugin={plugin}
                  onClick={() => onSelectPlugin(plugin)}
                  onClientClick={handleClientToggle}
                  onCategoryClick={handleCategoryToggle}
                />
              ))}
            </div>
          )}
          {sentinelVisible && <div ref={sentinelRef} aria-hidden="true" />}
        </>
      )}
    </div>
  );
}
